"use client";

import React, { useState, useEffect, useTransition } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getCapacityDataAction } from "@/actions/capacity-actions";
import {
  ChevronLeft,
  ChevronRight,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

interface AgentCapacity {
  agentId: string;
  agentName: string;
  role: string;
  baseCapacity: number;
  timeOffHours: number;
  netCapacity: number;
  loggedHours: number;
  workloadPercentage: number;
}

interface PeriodInfo {
  startDate: string;
  endDate: string;
  workingDays: number;
}

interface CapacityClientProps {
  initialData: AgentCapacity[];
  initialPeriod: PeriodInfo;
}

export function CapacityClient({
  initialData,
  initialPeriod,
}: CapacityClientProps) {
  const [data, setData] = useState<AgentCapacity[]>(initialData);
  const [period, setPeriod] = useState<PeriodInfo>(initialPeriod);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [capacityFilter, setCapacityFilter] = useState<
    "all" | "over" | "optimal"
  >("all");

  // Helper to format date
  const formatDateString = (isoString: string) => {
    return new Date(isoString).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStartOfWeek = (d: Date) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const getEndOfWeek = (d: Date) => {
    const start = getStartOfWeek(d);
    const sunday = new Date(start);
    sunday.setDate(start.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return sunday;
  };

  const loadCapacityData = (targetDate: Date) => {
    setIsLoading(true);
    const start = getStartOfWeek(targetDate);
    const end = getEndOfWeek(targetDate);

    startTransition(async () => {
      const res = await getCapacityDataAction({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });

      if (res.success && res.data && res.period) {
        setData(res.data);
        setPeriod(res.period);
      }
      setIsLoading(false);
    });
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
    loadCapacityData(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
    loadCapacityData(newDate);
  };

  // Team summary calculations
  const totalAgents = data.length;
  const avgWorkload =
    totalAgents > 0
      ? Math.round(
          data.reduce((sum, a) => sum + a.workloadPercentage, 0) / totalAgents,
        )
      : 0;

  const overAllocatedCount = data.filter(
    (a) => a.workloadPercentage > 100,
  ).length;
  const normalAllocatedCount = data.filter(
    (a) => a.workloadPercentage >= 50 && a.workloadPercentage <= 100,
  ).length;
  const underAllocatedCount = data.filter(
    (a) => a.workloadPercentage < 50,
  ).length;

  const filteredAgents = data.filter((a) => {
    if (capacityFilter === "over") return a.workloadPercentage > 100;
    if (capacityFilter === "optimal")
      return a.workloadPercentage >= 50 && a.workloadPercentage <= 100;
    return true;
  });

  return (
    <div className="space-y-8">
      {/* Date Navigation Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-card border border-border p-4 rounded-xl shadow-[var(--shadow-card)]">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Periodo di Capacità: {formatDateString(period.startDate)} -{" "}
            {formatDateString(period.endDate)}
          </h2>
          <p className="text-xs text-text-secondary mt-0.5">
            Calcolo basato su {period.workingDays} giorni lavorativi (8
            ore/giorno per risorsa).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevWeek}
            disabled={isLoading}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Settimana Prec.
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextWeek}
            disabled={isLoading}
          >
            Settimana Succ.
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          onClick={() => setCapacityFilter("all")}
          className={`cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
            capacityFilter === "all"
              ? "ring-2 ring-primary-600 border-primary-500 bg-primary-500/[0.01]"
              : ""
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Membri Attivi
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <span className="text-2xl font-bold text-foreground">
                {totalAgents}
              </span>
              <p className="text-[10px] text-text-secondary mt-1">
                Risorse umane monitorate
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
              <Users className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card
          onClick={() => setCapacityFilter("all")}
          className={`cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
            capacityFilter === "all"
              ? "ring-2 ring-primary-600 border-primary-500 bg-primary-500/[0.01]"
              : ""
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Carico Medio Team
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <span className="text-2xl font-bold text-foreground">
                {avgWorkload}%
              </span>
              <p className="text-[10px] text-text-secondary mt-1">
                Effort allocato medio
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <TrendingUp className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card
          onClick={() => setCapacityFilter("over")}
          className={`cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
            capacityFilter === "over"
              ? "ring-2 ring-error-600 border-error-500 bg-error-500/[0.01]"
              : ""
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Sovra-Allocati (&gt;100%)
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <span className="text-2xl font-bold text-foreground">
                {overAllocatedCount}
              </span>
              <p className="text-[10px] text-text-secondary mt-1">
                Rischio burnout immediato
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-error-50 text-error-600 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card
          onClick={() => setCapacityFilter("optimal")}
          className={`cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
            capacityFilter === "optimal"
              ? "ring-2 ring-green-600 border-green-500 bg-green-500/[0.01]"
              : ""
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Ottimali (50% - 100%)
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <span className="text-2xl font-bold text-foreground">
                {normalAllocatedCount}
              </span>
              <p className="text-[10px] text-text-secondary mt-1">
                Capacità bilanciata
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-text-secondary text-sm">
          Caricamento dati di capacità in corso...
        </div>
      ) : (
        <>
          {/* Workload Progress Meters */}
          <div>
            <h3 className="text-lg font-bold text-foreground mb-4">
              Carico di Lavoro Individuale
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAgents.map((agent) => {
                const workload = agent.workloadPercentage;
                let meterColor = "bg-primary-600"; // Default
                let textColor =
                  "text-primary-700 bg-primary-50 border-primary-100";

                if (workload > 100) {
                  meterColor = "bg-error";
                  textColor = "text-error-700 bg-error-50 border-error-100";
                } else if (workload >= 80) {
                  meterColor = "bg-amber-500";
                  textColor = "text-amber-700 bg-amber-50 border-amber-100";
                } else if (workload >= 50) {
                  meterColor = "bg-green-600";
                  textColor = "text-green-700 bg-green-50 border-green-100";
                } else {
                  meterColor = "bg-blue-500";
                  textColor = "text-blue-700 bg-blue-50 border-blue-100";
                }

                return (
                  <Card
                    key={agent.agentId}
                    className="flex flex-col justify-between"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">
                            {agent.agentName}
                          </CardTitle>
                          <CardDescription className="text-xs uppercase mt-0.5">
                            {agent.role}
                          </CardDescription>
                        </div>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded border ${textColor}`}
                        >
                          {workload}% allocato
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Visual Meter Bar */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs text-text-secondary">
                          <span>Progress</span>
                          <span>
                            {agent.loggedHours}h di {agent.netCapacity}h netti
                          </span>
                        </div>
                        <div className="w-full bg-card-muted rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${meterColor}`}
                            style={{ width: `${Math.min(100, workload)}%` }}
                          />
                        </div>
                      </div>

                      {/* Capacity details list */}
                      <div className="bg-card-muted/50 p-3 rounded-lg border border-border/40 text-xs space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-text-secondary">
                            Capacità Base:
                          </span>
                          <span className="font-medium text-foreground">
                            {agent.baseCapacity}h
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">
                            Ferie/Time Off Approvato:
                          </span>
                          <span className="font-medium text-error">
                            - {agent.timeOffHours}h
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-border/50 pt-1.5 font-semibold">
                          <span>Capacità Netta:</span>
                          <span className="text-foreground">
                            {agent.netCapacity}h
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">
                            Ore Registrate:
                          </span>
                          <span className="font-medium text-foreground">
                            {agent.loggedHours}h
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Matrix Details Table */}
          <Card>
            <CardHeader>
              <CardTitle>Tabella Analisi Capacità</CardTitle>
              <CardDescription>
                Visualizzazione tabellare dell'efficienza e delle assenze.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-card-muted/50 text-xs font-semibold text-text-secondary uppercase">
                      <th className="px-6 py-3">Agente</th>
                      <th className="px-6 py-3">Ruolo</th>
                      <th className="px-6 py-3 text-right">
                        Capacità Base (h)
                      </th>
                      <th className="px-6 py-3 text-right">
                        Ferie/Assenze (h)
                      </th>
                      <th className="px-6 py-3 text-right">
                        Capacità Netta (h)
                      </th>
                      <th className="px-6 py-3 text-right">Ore Loggate (h)</th>
                      <th className="px-6 py-3 text-right">Utilizzo %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {filteredAgents.map((agent) => {
                      const workload = agent.workloadPercentage;
                      let statusText = "text-green-600";
                      if (workload > 100) statusText = "text-error font-bold";
                      else if (workload >= 80)
                        statusText = "text-amber-600 font-medium";

                      return (
                        <tr
                          key={agent.agentId}
                          className="hover:bg-card-muted/20 transition-colors"
                        >
                          <td className="px-6 py-4 font-semibold text-foreground">
                            {agent.agentName}
                          </td>
                          <td className="px-6 py-4 text-xs text-text-secondary capitalize">
                            {agent.role}
                          </td>
                          <td className="px-6 py-4 text-right text-text-secondary">
                            {agent.baseCapacity}
                          </td>
                          <td className="px-6 py-4 text-right text-error">
                            - {agent.timeOffHours}
                          </td>
                          <td className="px-6 py-4 text-right font-medium text-foreground">
                            {agent.netCapacity}
                          </td>
                          <td className="px-6 py-4 text-right text-text-secondary">
                            {agent.loggedHours}
                          </td>
                          <td className={`px-6 py-4 text-right ${statusText}`}>
                            {workload}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
